import { create } from 'zustand'

export type ProductStatus = 'Stashed' | 'Not verified' | 'Earning' | 'At risk'

// Number of Stashed SKUs a dispensary needs to unlock Stash Seller status
// (better ranking + priority support).
export const STASH_SELLER_THRESHOLD = 5

export interface Dispensary {
  name: string
  // Position on the simplified competition map, as 0–100 percentages.
  x: number
  y: number
  // Seed count of Stashed SKUs. For the current dispensary this is recomputed
  // live from products so the gamified progress bar reacts to verifications.
  stashedSkus: number
  isCurrent?: boolean
}

export interface Product {
  id: string
  name: string
  brand: string
  category: string
  price: number
  originalPrice: number
  size: string
  rating: number
  reviewCount: number
  potency: string
  distance: string
  status: ProductStatus
  lastUpdated: string // e.g. "6 min ago", "3 days ago"
  dispensaryName: string
  inStock: boolean
  image: string
  failedPickups: number
  sponsored?: boolean
  // Inventory management
  stockCount: number // units physically on hand
  reorderPoint: number // low-stock threshold
  lastSaleAt: string // e.g. "8 min ago"
  recentSales: { time: string; qty: number; price: number }[]
}

export type OrderStatus = 'Reserved' | 'Picked up' | 'Flagged'

export interface Order {
  id: string
  productId: string
  shopperName: string
  reservedPrice: number
  reservedTime: string
  status: OrderStatus
}

interface StoreState {
  role: 'Shopper' | 'Dispensary'
  setRole: (role: 'Shopper' | 'Dispensary') => void

  products: Product[]
  orders: Order[]

  // Stash Seller program
  dispensaries: Dispensary[]
  currentDispensaryName: string

  posConnected: boolean
  setPosConnected: (connected: boolean) => void

  // Actions
  reserveProduct: (productId: string) => void
  markOrderPickedUp: (orderId: string) => void
  reportOrderChange: (orderId: string, reason: string) => void
  toggleProductStock: (productId: string) => void
  updateProductPrice: (productId: string, newPrice: number) => void
  updateProductStockCount: (productId: string, newCount: number) => void
  verifyProduct: (productId: string, inStock: boolean, price: number) => void
  // Pulls a mislabeled / wrong-SKU listing off the shelf for relisting.
  resolveWrongItem: (productId: string) => void

  // Shopper specific
  shopperCurrentView:
    | 'Browse'
    | 'Detail'
    | 'ReserveConfirm'
    | 'PostPickup'
    | 'ClaimResult'
  shopperSelectedProductId: string | null
  setShopperView: (
    view: StoreState['shopperCurrentView'],
    productId?: string,
  ) => void

  // Business specific
  businessCurrentView: 'Overview' | 'Menu' | 'Orders' | 'Settings'
  setBusinessView: (view: StoreState['businessCurrentView']) => void
}

const initialProducts: Product[] = [
  {
    id: 'p1',
    name: 'Blue Dream 1/8 oz',
    brand: 'Big Buds',
    category: 'Flower',
    price: 35,
    originalPrice: 50,
    size: '1/8 oz',
    rating: 4.9,
    reviewCount: 142,
    potency: '22% THC',
    distance: '1.2 mi',
    status: 'Not verified',
    lastUpdated: '3 days ago',
    dispensaryName: 'Verde Market',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1603909223358-c8c1c3f8b9b4?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    sponsored: true,
    stockCount: 6,
    reorderPoint: 8,
    lastSaleAt: '2 hours ago',
    recentSales: [
      { time: '2 hours ago', qty: 1, price: 35 },
      { time: 'Yesterday', qty: 2, price: 35 },
      { time: 'Yesterday', qty: 1, price: 35 },
    ],
  },
  {
    id: 'p2',
    name: 'Wedding Cake 1/8 oz',
    brand: 'Khalifa Kush',
    category: 'Flower',
    price: 45,
    originalPrice: 60,
    size: '1/8 oz',
    rating: 4.7,
    reviewCount: 38,
    potency: '26% THC',
    distance: '1.2 mi',
    status: 'Stashed',
    lastUpdated: '6 min ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1605196560547-b2f7281b7355?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 24,
    reorderPoint: 10,
    lastSaleAt: '6 min ago',
    recentSales: [
      { time: '6 min ago', qty: 1, price: 45 },
      { time: '38 min ago', qty: 1, price: 45 },
      { time: '1 hour ago', qty: 2, price: 45 },
    ],
  },
  {
    id: 'p3',
    name: 'Sleep gummies 10mg',
    brand: 'WYLD',
    category: 'Edibles',
    price: 20,
    originalPrice: 28,
    size: '10 pk',
    rating: 4.8,
    reviewCount: 211,
    potency: '100mg THC total',
    distance: '1.2 mi',
    status: 'Stashed',
    lastUpdated: '12 min ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    sponsored: true,
    stockCount: 42,
    reorderPoint: 15,
    lastSaleAt: '12 min ago',
    recentSales: [
      { time: '12 min ago', qty: 2, price: 20 },
      { time: '25 min ago', qty: 1, price: 20 },
      { time: '50 min ago', qty: 3, price: 20 },
    ],
  },
  {
    id: 'p4',
    name: 'Calm tincture 1:1',
    brand: 'Care By Design',
    category: 'Tinctures',
    price: 55,
    originalPrice: 70,
    size: '30 mL',
    rating: 4.5,
    reviewCount: 12,
    potency: '300mg THC / 300mg CBD',
    distance: '2.5 mi',
    status: 'Earning',
    lastUpdated: '1 hour ago',
    dispensaryName: 'Bloom St',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1556760544-74068565f05c?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 9,
    reorderPoint: 6,
    lastSaleAt: '1 hour ago',
    recentSales: [
      { time: '1 hour ago', qty: 1, price: 55 },
      { time: '3 hours ago', qty: 1, price: 55 },
    ],
  },
  {
    id: 'p5',
    name: 'Live resin cart',
    brand: 'ROVE',
    category: 'Vapes',
    price: 40,
    originalPrice: 55,
    size: '1 g',
    rating: 4.6,
    reviewCount: 89,
    potency: '80% THC',
    distance: '1.2 mi',
    status: 'At risk',
    lastUpdated: '2 hours ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1567861911437-538298e4232c?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 2,
    stockCount: 3,
    reorderPoint: 8,
    lastSaleAt: '2 hours ago',
    recentSales: [
      { time: '2 hours ago', qty: 1, price: 40 },
      { time: 'Yesterday', qty: 1, price: 40 },
    ],
  },
  {
    id: 'p6',
    name: 'Sativa pre-roll pack',
    brand: 'High Tide',
    category: 'Pre-rolls',
    price: 30,
    originalPrice: 45,
    size: '5 pk',
    rating: 4.7,
    reviewCount: 56,
    potency: '24% THC',
    distance: '3.0 mi',
    status: 'Stashed',
    lastUpdated: '4 min ago',
    dispensaryName: 'Terra',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1603909223429-69bb7101f420?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 18,
    reorderPoint: 10,
    lastSaleAt: '4 min ago',
    recentSales: [
      { time: '4 min ago', qty: 1, price: 30 },
      { time: '22 min ago', qty: 2, price: 30 },
      { time: '1 hour ago', qty: 1, price: 30 },
    ],
  },
  {
    id: 'p7',
    name: 'OG Kush 1/8 oz',
    brand: 'Big Buds',
    category: 'Flower',
    price: 38,
    originalPrice: 55,
    size: '1/8 oz',
    rating: 4.8,
    reviewCount: 97,
    potency: '25% THC',
    distance: '1.2 mi',
    status: 'Stashed',
    lastUpdated: '9 min ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1603386329225-868f9b1ee6c9?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 14,
    reorderPoint: 10,
    lastSaleAt: '9 min ago',
    recentSales: [
      { time: '9 min ago', qty: 1, price: 38 },
      { time: '40 min ago', qty: 1, price: 38 },
    ],
  },
  {
    id: 'p8',
    name: 'Raspberry gummies 5mg',
    brand: 'WYLD',
    category: 'Edibles',
    price: 18,
    originalPrice: 26,
    size: '10 pk',
    rating: 4.9,
    reviewCount: 312,
    potency: '50mg THC total',
    distance: '2.5 mi',
    status: 'Stashed',
    lastUpdated: '20 min ago',
    dispensaryName: 'Bloom St',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 56,
    reorderPoint: 20,
    lastSaleAt: '20 min ago',
    recentSales: [
      { time: '20 min ago', qty: 2, price: 18 },
      { time: '35 min ago', qty: 1, price: 18 },
      { time: '1 hour ago', qty: 4, price: 18 },
    ],
  },
  {
    id: 'p9',
    name: 'Hybrid disposable vape',
    brand: 'ROVE',
    category: 'Vapes',
    price: 45,
    originalPrice: 60,
    size: '1 g',
    rating: 4.5,
    reviewCount: 64,
    potency: '85% THC',
    distance: '3.0 mi',
    status: 'Earning',
    lastUpdated: '45 min ago',
    dispensaryName: 'Terra',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1605196560547-b2f7281b7355?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 11,
    reorderPoint: 8,
    lastSaleAt: '45 min ago',
    recentSales: [
      { time: '45 min ago', qty: 1, price: 45 },
      { time: '2 hours ago', qty: 1, price: 45 },
    ],
  },
  {
    id: 'p10',
    name: 'Infused pre-roll 1g',
    brand: 'High Tide',
    category: 'Pre-rolls',
    price: 14,
    originalPrice: 22,
    size: '1 g',
    rating: 4.6,
    reviewCount: 41,
    potency: '32% THC',
    distance: '3.0 mi',
    status: 'Not verified',
    lastUpdated: '4 days ago',
    dispensaryName: 'Higher Ground',
    inStock: false,
    image:
      'https://images.unsplash.com/photo-1603909223429-69bb7101f420?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 1,
    stockCount: 0,
    reorderPoint: 6,
    lastSaleAt: '4 days ago',
    recentSales: [{ time: '4 days ago', qty: 1, price: 14 }],
  },
  {
    id: 'p11',
    name: 'Relief tincture 2:1',
    brand: 'Care By Design',
    category: 'Tinctures',
    price: 48,
    originalPrice: 62,
    size: '30 mL',
    rating: 4.7,
    reviewCount: 28,
    potency: '200mg THC / 400mg CBD',
    distance: '2.5 mi',
    status: 'Stashed',
    lastUpdated: '15 min ago',
    dispensaryName: 'Bloom St',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1556760544-74068565f05c?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 7,
    reorderPoint: 6,
    lastSaleAt: '15 min ago',
    recentSales: [
      { time: '15 min ago', qty: 1, price: 48 },
      { time: '1 hour ago', qty: 1, price: 48 },
    ],
  },
  {
    id: 'p12',
    name: 'Gelato 1/8 oz',
    brand: 'Big Buds',
    category: 'Flower',
    price: 42,
    originalPrice: 58,
    size: '1/8 oz',
    rating: 4.8,
    reviewCount: 73,
    potency: '24% THC',
    distance: '1.2 mi',
    status: 'Stashed',
    lastUpdated: '8 min ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1603909223358-c8c1c3f8b9b4?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 21,
    reorderPoint: 10,
    lastSaleAt: '8 min ago',
    recentSales: [
      { time: '8 min ago', qty: 1, price: 42 },
      { time: '30 min ago', qty: 2, price: 42 },
    ],
  },
  {
    id: 'p13',
    name: 'Mango gummies 5mg',
    brand: 'WYLD',
    category: 'Edibles',
    price: 19,
    originalPrice: 27,
    size: '10 pk',
    rating: 4.9,
    reviewCount: 188,
    potency: '50mg THC total',
    distance: '1.2 mi',
    status: 'Stashed',
    lastUpdated: '5 min ago',
    dispensaryName: 'Green Cross',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 38,
    reorderPoint: 15,
    lastSaleAt: '5 min ago',
    recentSales: [
      { time: '5 min ago', qty: 2, price: 19 },
      { time: '40 min ago', qty: 1, price: 19 },
    ],
  },
  {
    id: 'p14',
    name: 'Sour Diesel 1/8 oz',
    brand: 'High Tide',
    category: 'Flower',
    price: 40,
    originalPrice: 54,
    size: '1/8 oz',
    rating: 4.7,
    reviewCount: 64,
    potency: '23% THC',
    distance: '2.0 mi',
    status: 'Stashed',
    lastUpdated: '11 min ago',
    dispensaryName: 'Verde Market',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1603386329225-868f9b1ee6c9?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 16,
    reorderPoint: 10,
    lastSaleAt: '11 min ago',
    recentSales: [
      { time: '11 min ago', qty: 1, price: 40 },
      { time: '1 hour ago', qty: 1, price: 40 },
    ],
  },
  {
    id: 'p15',
    name: 'Indica disposable vape',
    brand: 'ROVE',
    category: 'Vapes',
    price: 44,
    originalPrice: 59,
    size: '1 g',
    rating: 4.6,
    reviewCount: 52,
    potency: '84% THC',
    distance: '3.0 mi',
    status: 'Stashed',
    lastUpdated: '18 min ago',
    dispensaryName: 'Higher Ground',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1605196560547-b2f7281b7355?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 27,
    reorderPoint: 12,
    lastSaleAt: '18 min ago',
    recentSales: [
      { time: '18 min ago', qty: 1, price: 44 },
      { time: '2 hours ago', qty: 1, price: 44 },
    ],
  },
  {
    id: 'p16',
    name: 'Balance tincture 1:1',
    brand: 'Care By Design',
    category: 'Tinctures',
    price: 52,
    originalPrice: 66,
    size: '30 mL',
    rating: 4.7,
    reviewCount: 31,
    potency: '300mg THC / 300mg CBD',
    distance: '2.0 mi',
    status: 'Stashed',
    lastUpdated: '22 min ago',
    dispensaryName: 'Verde Market',
    inStock: true,
    image:
      'https://images.unsplash.com/photo-1556760544-74068565f05c?auto=format&fit=crop&q=80&w=300&h=300',
    failedPickups: 0,
    stockCount: 13,
    reorderPoint: 8,
    lastSaleAt: '22 min ago',
    recentSales: [
      { time: '22 min ago', qty: 1, price: 52 },
      { time: '1 hour ago', qty: 1, price: 52 },
    ],
  },
]

const initialDispensaries: Dispensary[] = [
  { name: 'Green Cross', x: 52, y: 56, stashedSkus: 5, isCurrent: true },
  { name: 'Bloom St', x: 28, y: 38, stashedSkus: 2 },
  { name: 'Terra', x: 72, y: 30, stashedSkus: 1 },
  { name: 'Verde Market', x: 20, y: 62, stashedSkus: 2 },
  { name: 'Higher Ground', x: 66, y: 64, stashedSkus: 1 },
  // Product-less competitor — its seed count drives the map (lone rival seller).
  { name: 'Emerald City', x: 40, y: 78, stashedSkus: 7 },
]

const initialOrders: Order[] = [
  {
    id: 'o1',
    productId: 'p1',
    shopperName: 'J*** S.',
    reservedPrice: 35,
    reservedTime: '10:30 AM',
    status: 'Reserved',
  },
  {
    id: 'o2',
    productId: 'p2',
    shopperName: 'A*** M.',
    reservedPrice: 45,
    reservedTime: '9:15 AM',
    status: 'Flagged',
  },
  {
    id: 'o3',
    productId: 'p3',
    shopperName: 'M*** T.',
    reservedPrice: 20,
    reservedTime: 'Yesterday',
    status: 'Picked up',
  },
]

export const useStore = create<StoreState>((set) => ({
  role: 'Shopper',
  setRole: (role) => set({ role }),

  products: initialProducts,
  orders: initialOrders,

  dispensaries: initialDispensaries,
  currentDispensaryName: 'Green Cross',

  posConnected: false,
  setPosConnected: (connected) => set({ posConnected: connected }),

  reserveProduct: (productId) =>
    set((state) => {
      const product = state.products.find((p) => p.id === productId)
      if (!product) return state

      const newOrder: Order = {
        id: `o${Date.now()}`,
        productId,
        shopperName: 'Y*** U.',
        reservedPrice: product.price,
        reservedTime: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        status: 'Reserved',
      }

      return {
        orders: [newOrder, ...state.orders],
        shopperCurrentView: 'ReserveConfirm',
      }
    }),

  markOrderPickedUp: (orderId) =>
    set((state) => ({
      orders: state.orders.map((o) =>
        o.id === orderId ? { ...o, status: 'Picked up' } : o,
      ),
    })),

  reportOrderChange: (orderId, reason) =>
    set((state) => {
      const order = state.orders.find((o) => o.id === orderId)
      if (!order) return state

      return {
        orders: state.orders.map((o) =>
          o.id === orderId ? { ...o, status: 'Flagged' } : o,
        ),
        products: state.products.map((p) => {
          if (p.id === order.productId) {
            const newFailed = p.failedPickups + 1
            return {
              ...p,
              failedPickups: newFailed,
              status: newFailed >= 3 ? 'Not verified' : 'At risk',
              lastUpdated: 'Just now',
            }
          }
          return p
        }),
      }
    }),

  toggleProductStock: (productId) =>
    set((state) => ({
      products: state.products.map((p) => {
        if (p.id === productId) {
          const newInStock = !p.inStock
          return {
            ...p,
            inStock: newInStock,
            status: newInStock ? 'Earning' : 'Not verified',
            failedPickups: 0, // Reset on manual action
            lastUpdated: 'Just now',
          }
        }
        return p
      }),
    })),

  updateProductPrice: (productId, newPrice) =>
    set((state) => ({
      products: state.products.map((p) => {
        if (p.id === productId) {
          return {
            ...p,
            price: newPrice,
            failedPickups: 0,
            status: 'Earning',
            lastUpdated: 'Just now',
          }
        }
        return p
      }),
    })),

  updateProductStockCount: (productId, newCount) =>
    set((state) => ({
      products: state.products.map((p) => {
        if (p.id === productId) {
          const count = Math.max(0, newCount)
          return {
            ...p,
            stockCount: count,
            inStock: count > 0,
            failedPickups: 0,
            status: count > 0 ? 'Earning' : 'Not verified',
            lastUpdated: 'Just now',
          }
        }
        return p
      }),
    })),

  verifyProduct: (productId, inStock, price) =>
    set((state) => ({
      products: state.products.map((p) => {
        if (p.id === productId) {
          return {
            ...p,
            inStock,
            price,
            failedPickups: 0,
            status: 'Earning', // Manual verification resets to Earning per spec
            lastUpdated: 'Just now',
          }
        }
        return p
      }),
    })),

  resolveWrongItem: (productId) =>
    set((state) => ({
      products: state.products.map((p) => {
        if (p.id === productId) {
          // A wrong SKU / mislabeled listing is pulled from the shelf so
          // shoppers can't reserve it until it's relisted correctly.
          return {
            ...p,
            inStock: false,
            stockCount: 0,
            failedPickups: 0,
            status: 'Not verified',
            lastUpdated: 'Just now',
          }
        }
        return p
      }),
    })),

  shopperCurrentView: 'Browse',
  shopperSelectedProductId: null,
  setShopperView: (view, productId) =>
    set((state) => ({
      shopperCurrentView: view,
      ...(productId !== undefined
        ? { shopperSelectedProductId: productId }
        : {}),
    })),

  businessCurrentView: 'Overview',
  setBusinessView: (view) => set({ businessCurrentView: view }),
}))

// ---- Stash Seller selectors -------------------------------------------------

export interface DispensaryStats extends Dispensary {
  // Effective Stashed-SKU count: live from products when the dispensary has
  // listings, otherwise the seed value (e.g. product-less competitors).
  stashedSkus: number
  isStashSeller: boolean
}

/** Number of Stashed SKUs a dispensary currently has, derived from products. */
export function getStashedCount(
  products: Product[],
  dispensaryName: string,
): number {
  return products.filter(
    (p) => p.dispensaryName === dispensaryName && p.status === 'Stashed',
  ).length
}

/** Has this dispensary earned Stash Seller status? */
export function isStashSeller(
  products: Product[],
  dispensaries: Dispensary[],
  dispensaryName: string,
): boolean {
  const seed = dispensaries.find((d) => d.name === dispensaryName)
  const hasProducts = products.some((p) => p.dispensaryName === dispensaryName)
  const count = hasProducts
    ? getStashedCount(products, dispensaryName)
    : (seed?.stashedSkus ?? 0)
  return count >= STASH_SELLER_THRESHOLD
}

/** Enriched dispensary list for the competition map. */
export function getDispensaryStats(
  products: Product[],
  dispensaries: Dispensary[],
): DispensaryStats[] {
  return dispensaries.map((d) => {
    const hasProducts = products.some((p) => p.dispensaryName === d.name)
    const stashedSkus = hasProducts
      ? getStashedCount(products, d.name)
      : d.stashedSkus
    return {
      ...d,
      stashedSkus,
      isStashSeller: stashedSkus >= STASH_SELLER_THRESHOLD,
    }
  })
}
